import { difference } from 'lodash';
import React from 'react';
import { FieldArray, FormikProps, useFormikContext } from 'formik';

import { differenceInCalendarMonths } from 'date-fns';
import { IUser, IJob, IResourcesStore } from '@src/models';
import {
  Title,
  AddButton,
  RemoveButton,
  FormikInput,
  FormikRadioButton,
  FormikCheckbox,
  FormikCombobox,
  FormikFileUploader,
  FormikSubmitButton
} from '@src/features/common';
import { validateFormikForm } from 'src/helpers';
import { BaseBox, Flex, Delimiter, StyledError } from 'src/kit';
import { ApplicationConfirmationModal } from './ApplicationConfirmationModal';
import { CustomQuestion } from './CustomQuestion';
import { getDefaultLanguageValue, allowedResumeExtensions, COVER_LETTER_MAX_WORDS } from './validationSchema';

type CVFormProps = FormikProps<any> & {
  resourcesStore: IResourcesStore;
  user: IUser;
  isFetching: boolean;
  job: IJob;
  refetchCv: () => void;
};

const CVForm = ({ resourcesStore, user, job, isFetching, refetchCv }: CVFormProps) => {
  const {
    values,
    isSubmitting: isSubmittingForm,
    setFieldValue,
    validateForm,
    setErrors,
    setTouched,
    submitForm
  } = useFormikContext();
  const [showApplicationConfirmationModal, setShowApplicationConfirmationModal] = React.useState(false);
  const [isConfirmed, setIsConfirmed] = React.useState(false);
  const isSubmitting = isFetching || isSubmittingForm;
  const handleSubmit = async () => {
    if (!isConfirmed && job.company?.allow_disclaimer && job.company?.disclaimer_url) {
      await validateFormikForm({ validateForm, setErrors, setTouched });
      setShowApplicationConfirmationModal(true);
    } else {
      submitForm();
    }
  };

  const handleConfirm = () => {
    setIsConfirmed(true);
    submitForm();
  };

  React.useEffect(() => {
    refetchCv();
  }, []);

  // how old your CV
  const diffInMonths = differenceInCalendarMonths(new Date(), new Date(user?.resume_updated_at));

  return (
    <div>
      {showApplicationConfirmationModal && (
        <ApplicationConfirmationModal
          job={job}
          onConfirm={handleConfirm}
          onClose={() => setShowApplicationConfirmationModal(false)}
        />
      )}
      <Title>Many thanks {values.first_name}! Just a few last questions on you:</Title>
      <Delimiter my={30} />
      <Flex mb={20}>
        <FormikRadioButton
          id="male"
          name="gender"
          disabled={values.preferNotToSayGender}
          variant="secondary"
          size="md"
          width="120px"
        >
          Male
        </FormikRadioButton>
        <FormikRadioButton
          id="female"
          name="gender"
          disabled={values.preferNotToSayGender}
          variant="secondary"
          size="md"
          width="120px"
        >
          Female
        </FormikRadioButton>
      </Flex>
      <FormikCheckbox name="preferNotToSayGender" mb={30}>
        Prefer not to say
      </FormikCheckbox>
      {(job.position_title) && (
        job.position_title.toLowerCase().includes("intern") ||
        job.position_title.toLowerCase().includes("internship") ||
        job.position_title.toLowerCase().includes("alternance") ||
        job.position_title.toLowerCase().includes("alternant") ||
        job.position_title.toLowerCase().includes("stage") ||
        job.position_title.toLowerCase().includes("stagiaire") ||
        job.position_title.toLowerCase().includes("apprenti") ||
        job.position_title.toLowerCase().includes("apprentissage")
      ) && (<Flex flexDirection="column" rowGap={10} mb={30}>
        <Flex flexDirection="row" columnGap={50}>
          <div style={{ display: "flex", flexDirection: "column", rowGap: 5 }}>
            <Flex>
              <FormikRadioButton
                id="true"
                name="is_first_internship"
                variant="secondary"
                size="md"
                width="120px"
              >
                Yes
              </FormikRadioButton>
              <FormikRadioButton
                id="false"
                name="is_first_internship"
                variant="secondary"
                size="md"
                width="120px"
              >
                No
              </FormikRadioButton>
            </Flex>
            <div>
              is this your first internship?
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", rowGap: 5 }}>
            <Flex>
              <FormikRadioButton
                id="true"
                name="has_convention"
                variant="secondary"
                size="md"
                width="120px"
              >
                Yes
              </FormikRadioButton>
              <FormikRadioButton
                id="false"
                name="has_convention"
                variant="secondary"
                size="md"
                width="120px"
              >
                No
              </FormikRadioButton>
            </Flex>
            <Flex flexDirection="column" rowGap={1}>
              <div>
                Do you have a valid internship agreement from your university?
              </div>
            </Flex>
          </div>
        </Flex>
      </Flex>
        )
      }
      {values.has_convention === 'false' && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          backgroundColor: '#d9534f',
          color: 'white',
          textAlign: 'center',
          padding: '20px',
          zIndex: 1000
        }}>
          NB: We reserve the right to reject applications without a valid agreement.
        </div>
      )}
      {job.application_form_settings?.languages && (
        <FieldArray name="languages_attributes">
          {arrayHelpers => (
            <BaseBox mb={20}>
              {values.languages_attributes.map((language, index, array) => (
                <Flex key={language.id || language.cid} mb={20}>
                  <Flex flex="0 1 100%">
                    <FormikCombobox
                      name={`languages_attributes.${index}.name`}
                      label="Language"
                      placeholder="Language"
                      items={difference(
                        resourcesStore.languages,
                        array.map(({ name }) => name)
                      )}
                      mr={30}
                      flex="1 1 0%"
                    />
                    <FormikCombobox
                      name={`languages_attributes.${index}.level`}
                      label="Level"
                      placeholder="Level"
                      items={resourcesStore.languagesLevels}
                      flex="1 1 0%"
                    />
                  </Flex>
                  {array.length > 1 && <RemoveButton onClick={() => arrayHelpers.remove(index)} mt={24} ml={20} />}
                </Flex>
              ))}
              <AddButton onClick={() => arrayHelpers.push(getDefaultLanguageValue())}>Add another language</AddButton>
            </BaseBox>
          )}
        </FieldArray>
      )}
      <FormikFileUploader
        name="cv_file"
        fileNames={[user?.resume_filename].filter(Boolean)}
        text="Drag and drop your resume/CV here"
        allowedExtensions={allowedResumeExtensions}
        onRemoveFile={() => {
          setFieldValue('resume_filename', null);
          setFieldValue('cv_file', null);
        }}
        mb={40}
      ></FormikFileUploader>
      {diffInMonths > 0 && user?.resume_filename && (
        <StyledError
          style={{ marginTop: -30, marginBottom: 40 }}
        >{`Your CV was uploaded ${diffInMonths} months ago, please check that this is the latest version of your CV`}</StyledError>
      )}

      <FormikInput name="linkedin" label="LinkedIn" size="md" mb={20} />
      {(job.application_form_settings?.cover_letter ||
        Boolean(job.application_form_settings?.formatted?.custom_questions?.length)) && (
          <>
            <Delimiter my={30} />
            <Title>Other details</Title>
            {job.application_form_settings?.cover_letter && (
              <FormikInput
                as="textarea"
                isFast
                name="cover_letter"
                label={
                  <Flex justifyContent="space-between">
                    <div>Cover letter</div>
                    <div>{COVER_LETTER_MAX_WORDS - (values.cover_letter?.split(' ').length || 0)}</div>
                  </Flex>
                }
                placeholder="Cover letter"
                mb={30}
                size="baseTextarea"
                inputProps={{ height: 250 }}
              />
            )}
            {Boolean(job.application_form_settings?.formatted?.custom_questions?.length) && (
              <BaseBox as="ol" m={0} p={0} css={{ listStyle: 'none' }}>
                {job.application_form_settings.formatted.custom_questions.map((question, index) => (
                  <li key={index}>
                    <CustomQuestion {...{ values, question, index }} mb={20} />
                  </li>
                ))}
              </BaseBox>
            )}
          </>
        )}
      <Delimiter my={30} />
      <Flex justifyContent="flex-end">
        <FormikSubmitButton type="button" onClick={handleSubmit} variant="primary" size="md">
          {isSubmitting ? 'Applying...' : 'Apply'}
        </FormikSubmitButton>
      </Flex>
    </div>
  );
};

export default CVForm;
